import { useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { MdKeyboardArrowLeft } from 'react-icons/md';
import useFetchJobSourcesServices from '@/features/agent/linked_accounts/api/useFetchJobSourcesServices';
import useFetchJobSources from '@/features/agent/linked_accounts/api/useFetchJobSources';
import LinkedAccountSelectService from '@/features/agent/linked_accounts/components/LinkedAccountSelectService';
import LinkedAccountSourceMethodSelect from '@/features/agent/linked_accounts/components/LinkedAccountSourceMethodSelect';
import JobSourceCredentialForm from '@/features/agent/linked_accounts/components/forms/JobSourceCredentialForm';
import JobSourceUrlForm from '@/features/agent/linked_accounts/components/forms/JobSourceUrlForm';
import LinkedAccountLoading from '@/features/agent/linked_accounts/components/LinkedAccountLoading';
import useCreateJobSource, {
  isPollingSourceType,
} from '@/features/agent/linked_accounts/hooks/useCreateJobSource';
import {
  findGroupServices,
  listPathFor,
  resolveServiceDisplayName,
} from '@/features/agent/linked_accounts/utils/serviceGroup';
import {
  linkedAccountModalStepAtom,
  linkedAccountMultiStepAtom,
} from '@/features/agent/linked_accounts/atoms/linkedAccountsAtom';
import Loading from '@/components/Loading';
import type { AgentApiJobSourcesService, JobSourceServiceType } from '@/types/serializers';
import WalkthroughLayout from './WalkthroughLayout';

type Props = {
  onComplete: () => void;
};

export default function WalkthroughJobSourceSetup({ onComplete }: Props) {
  const [step, setStep] = useAtom(linkedAccountModalStepAtom);
  const [multiStep, setMultiStep] = useAtom(linkedAccountMultiStepAtom);

  // マウント時にatomsを初期化
  useEffect(() => {
    setStep(1);
    setMultiStep(1);
  }, [setStep, setMultiStep]);

  const { data: services, isPending: isLoadingServices } = useFetchJobSourcesServices();
  const { refetch } = useFetchJobSources('normal');
  const [selectedService, setSelectedService] = useState<{
    name: string;
    imageUrl: string;
    type: 'multi' | 'auth';
    sourceType?: string;
  } | null>(null);
  const [selectedServiceDetail, setSelectedServiceDetail] = useState<
    AgentApiJobSourcesService | undefined
  >(undefined);

  const { createWithCredentials, createWithUrls, isPending, isPolling } = useCreateJobSource();
  const [pollingServiceType, setPollingServiceType] = useState<JobSourceServiceType>();

  const handleServiceSelect = (service: {
    name: string;
    imageUrl: string;
    type: 'multi' | 'auth';
    sourceType?: string;
  }) => {
    setSelectedService(service);
    setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) {
      if (selectedService?.type === 'multi') {
        if (multiStep === 2) {
          setMultiStep(1);
        } else {
          setStep(step - 1);
        }
      } else {
        setStep(step - 1);
      }
    }
  };

  const handleCreated = async (result: { status: 'success' | 'polling' | 'error' }) => {
    if (result.status !== 'success') return;
    await refetch();
    onComplete();
  };

  const renderForm = (service: AgentApiJobSourcesService) => {
    if (isPolling && isPollingSourceType(service.source_type)) {
      return <LinkedAccountLoading listPath={listPathFor(pollingServiceType)} />;
    }

    const isUrlType = service.auth_type === 'URL' || service.auth_type === 'AGENT_URL';

    return isUrlType ? (
      <JobSourceUrlForm
        service={service}
        isSubmitting={isPending}
        onSubmit={async (values) =>
          handleCreated(
            await createWithUrls({
              sourceType: service.source_type,
              urls: values.urls,
              loginId: values.loginId,
              loginPass: values.loginPass,
            })
          )
        }
      />
    ) : (
      <JobSourceCredentialForm
        service={service}
        serviceName={resolveServiceDisplayName(service.source_type, service)}
        isSubmitting={isPending}
        onSubmit={async (values) => {
          const result = await createWithCredentials({
            sourceType: service.source_type,
            loginId: values.loginId,
            loginPass: values.loginPass,
            accessUrl: values.accessUrl || undefined,
            basicAuthId: values.basicAuthId || undefined,
            basicAuthPass: values.basicAuthPass || undefined,
          });
          if (result.status === 'polling') setPollingServiceType(result.serviceType);
          await handleCreated(result);
        }}
      />
    );
  };

  return (
    <WalkthroughLayout>
      <div className="relative flex h-full flex-col items-center justify-center">
        {/* 戻るボタン */}
        {step > 1 && (
          <button
            onClick={handleBack}
            className="absolute top-8 left-8 z-10 flex cursor-pointer items-center gap-1 rounded-full bg-white p-2 shadow-[0px_0px_6px_0px_rgba(0,0,0,0.2)] hover:opacity-60"
            aria-label="戻る"
            type="button"
          >
            <MdKeyboardArrowLeft size={32} color="#818B9A" />
          </button>
        )}

        {/* コンテンツ */}
        <div className="flex items-center justify-center">
          {step === 1 && <LinkedAccountSelectService onServiceSelect={handleServiceSelect} />}
          {step === 2 && selectedService?.type === 'multi' && multiStep === 1 && (
            <LinkedAccountSourceMethodSelect
              groupName={selectedService.name}
              services={findGroupServices(services ?? [], selectedService.name)}
              onSelect={(service) => {
                setSelectedServiceDetail(service);
                setMultiStep(2);
              }}
            />
          )}
          {step === 2 &&
            selectedService?.type === 'multi' &&
            multiStep === 2 &&
            selectedServiceDetail &&
            renderForm(selectedServiceDetail)}
          {step >= 2 &&
            selectedService?.type === 'auth' &&
            (() => {
              const service = (services ?? []).find(
                (s) => s.source_type === selectedService.sourceType
              );
              if (service) {
                return renderForm(service);
              }
              if (isLoadingServices) {
                return (
                  <div className="py-3xl flex flex-col items-center justify-center gap-md">
                    <Loading size="medium" />
                    <p className="text-body-sm text-[#6D6D6D]">連携設定を読み込み中です</p>
                  </div>
                );
              }
              return null;
            })()}
        </div>
      </div>
    </WalkthroughLayout>
  );
}
